import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, BookOpen } from "lucide-react";
import { KolamGrid, KolamLoop, Overline } from "./Motifs";
import { MEERA_BOOKS } from "../data/content";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* Kolam dot grid background */}
      <div className="absolute top-24 right-6 w-[280px] h-[280px] text-[#1E2A4F] opacity-10 kolam-bg pointer-events-none">
        <KolamGrid cols={9} rows={9} className="w-full h-full" />
      </div>
      <div className="absolute -bottom-10 left-4 w-[180px] h-[180px] text-[#D95D39] opacity-20 pointer-events-none">
        <KolamLoop className="w-full h-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <Overline>A storyteller from Bangalore</Overline>
            <h1 className="font-display mt-6 text-[14vw] sm:text-7xl lg:text-[6.2rem] leading-[0.95] tracking-tight text-[#1E2A4F]">
              <span className="block text-[#D95D39] font-display italic font-light text-2xl sm:text-3xl lg:text-4xl mb-3 not-italic">
                Dr.
              </span>
              Divya
              <br />
              <span className="italic font-light">Ramachandran</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[#5C6A8F] leading-relaxed">
              Author • Educator • Researcher • Artist • Musician. <br className="hidden sm:block" />
              Telling stories that teach children — and grown-ups — to look at the world again.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#meera"
                data-testid="hero-cta-meera"
                className="group inline-flex items-center gap-2 px-6 py-3.5 bg-[#D95D39] text-[#F9F6F0] rounded-full text-sm tracking-wide hover:bg-[#1E2A4F] transition-colors duration-500"
              >
                <BookOpen size={16} />
                Explore the Meera Series
                <ArrowDownRight
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1"
                />
              </a>
              <a
                href="#about"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-[#1E2A4F]/30 text-[#1E2A4F] rounded-full text-sm tracking-wide hover:border-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-all duration-500"
              >
                About the Author
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 text-sm tracking-wide text-[#1E2A4F] ink-link"
              >
                Contact
              </a>
            </div>
          </motion.div>

          {/* Featured Meera book — large */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-3 bg-[#F3EDE4] rounded-lg -rotate-2"></div>
              <div className="absolute -inset-1 bg-[#1E2A4F] rounded-lg rotate-1"></div>
              <div className="relative overflow-hidden rounded-lg border border-[#E5DFD3] bg-white">
                <img
                  src={MEERA_BOOKS[0].cover}
                  alt={MEERA_BOOKS[0].title}
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E2A4F]/85 via-[#1E2A4F]/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-[#F9F6F0]">
                  <Overline className="!text-[#E76F51]">Featured Book</Overline>
                  <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">
                    Meera and the Magic Kolam
                  </h3>
                  <p className="text-xs sm:text-sm opacity-80 mt-2 tracking-wide">
                    Book One in Meera and her Artistic Adventures
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Series strap */}
        <div className="mt-20 grid sm:grid-cols-3 gap-8 sm:gap-12 border-t border-[#E5DFD3] pt-10">
          {[
            { k: "Series", v: "Meera and her Artistic Adventures" },
            { k: "For Readers", v: "Ages 5–10" },
            { k: "About", v: "India's living art forms, retold for young hearts" },
          ].map((s) => (
            <div key={s.k}>
              <Overline>{s.k}</Overline>
              <p className="font-display text-xl sm:text-2xl text-[#1E2A4F] mt-3 leading-snug">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
